import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { fetchCalibrations, fetchLoadTest, fetchPastTestData } from '../services/api';
import { readShowUnload, shownSteps, writeShowUnload } from '../load_test_components/steps';
import { readSmoothing, writeSmoothing } from '../load_test_components/smoothing';

const LoadTestDashboardContext = createContext();

export function LoadTestDashboardProvider({ children }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadTestData, setLoadTestData] = useState({});
  const [loadGraphData, setLoadGraphData] = useState({});
  const [loadTimeGraphData, setLoadTimeGraphData] = useState({});
  const [pressureTimeGraphData, setPressureTimeGraphData] = useState({});
  const [lvdtTimeGraphData, setLvdtTimeGraphData] = useState({});
  // Every step of the test, load and unload; the dashboard shows the ones
  // shownSteps picks (stepSummaryData / strainGraphData below).
  const [allStrainGraphData, setStrainGraphData] = useState([]);
  const [allStepSummaryData, setStepSummaryData] = useState([]);
  const [showUnloadSteps, setShowUnloadStepsState] = useState(readShowUnload);
  const setShowUnloadSteps = useCallback((show) => {
    writeShowUnload(show);
    setShowUnloadStepsState(show);
  }, []);
  const stepSummaryData = useMemo(() => shownSteps(allStepSummaryData, showUnloadSteps), [allStepSummaryData, showUnloadSteps]);
  const strainGraphData = useMemo(() => shownSteps(allStrainGraphData, showUnloadSteps), [allStrainGraphData, showUnloadSteps]);
  // How much the line graphs are smoothed, 0 (raw) to 100 percent; display
  // only (see load_test_components/smoothing.js). Remembered per browser.
  const [smoothing, setSmoothingState] = useState(readSmoothing);
  const setSmoothing = useCallback((percent) => {
    writeSmoothing(percent);
    setSmoothingState(percent);
  }, []);
  // Where the test stands (see TEST_STATUS in load_test_components/steps.js).
  const [testStatus, setTestStatus] = useState(null);
  const [activeStrainGauges, setActiveStrainGauges] = useState(4);
  const [currentReadings, setCurrentReadings] = useState({});
  const [plateAngles, setPlateAngles] = useState({});
  const [lvdtGraphData, setLvdtGraphData] = useState([]);
  const [projectDetails, setProjectDetails] = useState({});
  const [saveOptionsAnchor, setSaveOptionsAnchor] = useState(null);
  const [selectedBox, setSelectedBox] = useState(null);
  const [testStrainGauges, setTestStrainGauges] = useState([]);
  const [measurementUnits, setMeasurementUnits] = useState({});
  const [dataWarnings, setDataWarnings] = useState([]);
  const [criticalDataWarnings, setCriticalDataWarnings] = useState([]);


  // Strain gauge pool (serial + latest calibration) for the dashboard's gauge
  // picker, loaded from the calibration tables. The endpoint is admin-gated, so
  // for other roles this stays empty and gauges are typed in by hand.
  const [strainGaugeOptions, setStrainGaugeOptions] = useState([]);
  // Also called after a save adds a gauge to the sensor records, so the picker
  // offers it straight away.
  const reloadStrainGaugeOptions = useCallback(() => {
    fetchCalibrations()
      .then((data) => setStrainGaugeOptions(data?.optionsData?.['Strain Gauges'] || []))
      .catch(() => setStrainGaugeOptions([]));
  }, []);
  useEffect(() => { reloadStrainGaugeOptions(); }, [reloadStrainGaugeOptions]);

  // The test on screen ({ id, start_time, end_time, revision: { id, rev_number } })
  // and the calibration settings its numbers were converted with, both from the
  // get-lt-data payload. null until a payload that carries them arrives.
  const [loadTest, setLoadTest] = useState(null);
  const [calibration, setCalibration] = useState(null);

  // A calibration being tried out before it is saved or applied:
  // { settings, note, baseRevisionId, dirty }. While it is set the dashboard
  // shows the preview's numbers and polled data is held back, so a refresh
  // can't swap the saved numbers back in underneath it. The ref is what the
  // poll callback reads, since it closes over an older render.
  const [calPreview, setCalPreviewState] = useState(null);
  const calPreviewRef = useRef(null);
  const setCalPreview = useCallback((preview) => {
    calPreviewRef.current = preview;
    setCalPreviewState(preview);
  }, []);

  // Whether the Calibration window is open. Here rather than in the header's
  // button, which is rebuilt when the header changes layout (see CalibrationHost).
  const [calDialogOpen, setCalDialogOpen] = useState(false);

  // A past test opened from Historical Data, { testId, revisionId }, or null on
  // the live dashboard. Calibration requests then answer for that test
  // (calView 'test'), and `viewing` is the revision shown and the one in use.
  const [historyView, setHistoryView] = useState(null);
  const [viewing, setViewing] = useState(null);
  const calView = historyView ? 'test' : undefined;

  // A preview belongs to the box it was started on.
  useEffect(() => {
    setCalPreview(null);
    setLoadTest(null);
    setCalibration(null);
  }, [selectedBox?.id, setCalPreview]);

  // Put one get-lt-data payload (or a preview / save / apply response in the
  // same shape) on screen.
  const applyDashboardData = useCallback((data) => {
    setLoadTestData(data['timeseries']);
    setLoadGraphData(data['deflection_graph']);
    setStrainGraphData(Array.isArray(data['strain_graph']) ? data['strain_graph'] : []);
    setStepSummaryData(Array.isArray(data['step_summary']) ? data['step_summary'] : []);
    setTestStatus(data['test_status'] || null);
    setActiveStrainGauges(data['active_strain_gauges']);
    setCurrentReadings(data['current_readings']);
    setProjectDetails(data['project_details']);
    setPlateAngles(data['plate_angles']);
    setLvdtGraphData(data['lvdt_graph'] || []);
    setLoadTest(data['test'] || null);
    setCalibration(data['calibration'] || null);
    setMeasurementUnits(data.measurement_units ?? {});
    setViewing(data['viewing'] || null);
  }, []);

  // The page's own fetches go through here.
  const applyPolledData = useCallback((data) => {
    if (calPreviewRef.current) return;
    applyDashboardData(data);
  }, [applyDashboardData]);

  // Drop a preview and show the saved numbers again.
  const endCalPreview = useCallback(async () => {
    setCalPreview(null);
    if (!historyView && !selectedBox?.id) return;
    try {
      const data = historyView
        ? await fetchPastTestData(historyView.testId, historyView.revisionId)
        : await fetchLoadTest(selectedBox.id);
      if (!calPreviewRef.current) applyDashboardData(data);
    } catch (err) {
      console.error(err);
    }
  }, [selectedBox?.id, historyView, setCalPreview, applyDashboardData]);

  // Strain gauge settings for the strain graph, from the calibration on screen.
  // Each gauge: { SerialNumber, CalibrationFactor, DepthM, excluded, channel }.
  // Rev 0 is the automatic baseline nobody has reviewed, so the graph treats
  // the gauge list as confirmed (plot by depth, skip excluded and uncalibrated
  // gauges) only for a saved revision or a preview.
  const strainGauges = useMemo(() => (calibration?.strain_gauges || []).map((g) => ({
    channel: g.channel,
    SerialNumber: g.serial || '',
    CalibrationFactor: g.factor ?? '',
    DepthM: g.depth_m,
    excluded: Boolean(g.excluded),
  })), [calibration]);
  const sgConfirmed = Boolean(calPreview) || (loadTest?.revision?.rev_number ?? 0) > 0;

  const tempTestBoxes = [
    { id: 1, Name: 'Test Box 1', JobNumber: 'North Harbor Expansion', online: true },
    { id: 2, Name: 'Test Box 2', JobNumber: '00-0002', online: true },
    { id: 3, Name: 'Test Box 3', JobNumber: '00-0003', online: true },
    { id: 4, Name: 'Test Box 4', JobNumber: '00-0004', online: false },
    { id: 5, Name: 'Test Box 5', JobNumber: '00-0005', online: false },
    { id: 6, Name: 'Test Box 6', JobNumber: '00-0006', online: false },
    { id: 7, Name: 'Test Box 7', JobNumber: '00-0007', online: false },
    { id: 8, Name: 'Test Box 8', JobNumber: '00-0008', online: false },
    { id: 9, Name: 'Test Box 9', JobNumber: '00-0009', online: false },
    { id: 10, Name: 'Test Box 10', JobNumber: '00-0010', online: false },
    { id: 11, Name: 'Test Box 11', JobNumber: '00-0011', online: false },
    { id: 12, Name: 'Test Box 12', JobNumber: '00-0012', online: false }
  ];

  const [selectedLoadTestBox, setSelectedLoadTestBox] = useState(tempTestBoxes[0]);

  const value = {
    loading, setLoading,
    error, setError,
    selectedLoadTestBox, setSelectedLoadTestBox,
    loadTestData, setLoadTestData,
    loadGraphData, setLoadGraphData,
    loadTimeGraphData, setLoadTimeGraphData,
    pressureTimeGraphData, setPressureTimeGraphData,
    lvdtTimeGraphData, setLvdtTimeGraphData,
    strainGraphData, allStrainGraphData, setStrainGraphData,
    stepSummaryData, allStepSummaryData, setStepSummaryData,
    showUnloadSteps, setShowUnloadSteps,
    smoothing, setSmoothing,
    testStatus,
    activeStrainGauges, setActiveStrainGauges,
    currentReadings, setCurrentReadings,
    plateAngles, setPlateAngles,
    lvdtGraphData, setLvdtGraphData,
    projectDetails, setProjectDetails,
    saveOptionsAnchor, setSaveOptionsAnchor,
    selectedBox, setSelectedBox,
    measurementUnits, setMeasurementUnits,
    strainGaugeOptions, reloadStrainGaugeOptions,
    strainGauges, sgConfirmed,
    loadTest, calibration,
    calPreview, setCalPreview, endCalPreview,
    calDialogOpen, setCalDialogOpen,
    historyView, setHistoryView, viewing, calView,
    applyDashboardData, applyPolledData,
    dataWarnings, setDataWarnings,
    criticalDataWarnings, setCriticalDataWarnings,
  };

  return (
    <LoadTestDashboardContext.Provider value={value}>
      {children}
    </LoadTestDashboardContext.Provider>
  )
}

export function useAppState() {
  return useContext(LoadTestDashboardContext);
}